import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { DashboardLayout } from "../../components/DashboardLayout";
import { links } from "./SuperAdminPages";
import api from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, X, MapPin } from "lucide-react";

export default function SuperAdminClinicEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [planChange, setPlanChange] = useState({ plan_id: "", reason: "" });

  useEffect(() => {
    api.get(`/admin/clinics/${id}/detail`).then((r) => {
      const c = r.data;
      setForm({
        name: c.name || "",
        slug: c.slug || "",
        phone: c.phone || "",
        address: c.address || "",
        email: c.email || "",
        website: c.website || "",
        description: c.description || "",
        logo_url: c.logo_url || "",
        online_booking_enabled: c.online_booking_enabled !== false,
        patient_portal_enabled: c.patient_portal_enabled !== false,
        state: c.state || "", district: c.district || "", city: c.city || "", locality: c.locality || "", postal_code: c.postal_code || "", landmark: c.landmark || "",
        latitude: c.latitude ?? "", longitude: c.longitude ?? "", public_directory_enabled: !!c.public_directory_enabled,
        departments: c.departments || [],
      });
      setSubscription({ plan_name: c.plan_name, plan_id: c.plan_id, status: c.subscription_status, renewal_date: c.renewal_date });
      setPlanChange({ plan_id: c.plan_id || "", reason: "" });
    }).catch((e) => toast.error(e?.response?.data?.detail || "Failed to load clinic"));
  }, [id]);
  useEffect(() => { api.get("/public/directory/departments").then((r) => setDepartmentOptions(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/plans").then((r) => setPlans((r.data || []).filter((plan) => plan.status === "active"))).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const uploadLogo = (file) => { if (!file) return; const reader = new FileReader(); reader.onload = async () => { try { const r = await api.post(`/admin/clinics/${id}/logo`, { data_url: reader.result }); set("logo_url", r.data.logo_url); toast.success("Logo uploaded"); } catch (e) { toast.error(e?.response?.data?.detail || "Logo upload failed"); } }; reader.readAsDataURL(file); };
  const useCurrentLocation = () => navigator.geolocation?.getCurrentPosition(({ coords }) => { setForm(f => ({ ...f, latitude: Number(coords.latitude.toFixed(6)), longitude: Number(coords.longitude.toFixed(6)) })); toast.success("Current location added"); }, () => toast.error("Location permission was not granted"), { enableHighAccuracy: true, timeout: 12000 });

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const payload = { ...form }; if (payload.latitude === "" || payload.longitude === "") { delete payload.latitude; delete payload.longitude; }
      await api.patch(`/admin/clinics/${id}`, payload);
      if (planChange.plan_id && planChange.plan_id !== subscription?.plan_id) {
        if (!planChange.reason.trim()) throw new Error("Provide a reason for changing the package");
        await api.post(`/subscriptions/clinic/${id}/change-plan`, {
          plan_id: planChange.plan_id,
          reason: planChange.reason.trim(),
          start_trial: false,
          notes: "Changed from the Super Admin clinic edit screen",
        });
      }
      toast.success("Saved");
      navigate(`/super-admin/clinics/${id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Save failed");
    }
    setBusy(false);
  };

  if (!form) return (
    <DashboardLayout links={links} title="Edit clinic">
      <div className="mg-card text-center text-slate-400 py-10">Loading…</div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout links={links} title="Edit clinic">
      <div className="flex items-center justify-between mb-4">
        <Link to={`/super-admin/clinics/${id}`} className="text-sm text-mg-medical font-semibold inline-flex items-center gap-1" data-testid="back-to-detail">
          <ArrowLeft className="w-4 h-4" /> Cancel
        </Link>
      </div>
      <form onSubmit={submit} className="mg-card space-y-4 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Clinic name *">
            <input value={form.name} onChange={(e) => set("name", e.target.value)} className="mg-input" required data-testid="field-name" />
          </Field>
          <Field label="Slug *">
            <input value={form.slug} onChange={(e) => set("slug", e.target.value)} className="mg-input" required data-testid="field-slug" />
          </Field>
          <Field label="Email">
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mg-input" data-testid="field-email" />
          </Field>
          <Field label="Phone">
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mg-input" data-testid="field-phone" />
          </Field>
          <Field label="Website">
            <input value={form.website} onChange={(e) => set("website", e.target.value)} className="mg-input" data-testid="field-website" />
          </Field>
          <Field label="Clinic logo">
            <div className="flex items-center gap-3">{form.logo_url && <img src={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${form.logo_url}`} alt="Clinic logo" className="w-14 h-14 rounded-lg object-contain border" />}<input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => uploadLogo(e.target.files?.[0])} className="mg-input" data-testid="field-logo" /></div>
          </Field>
          <Field label="Address" full>
            <input value={form.address} onChange={(e) => set("address", e.target.value)} className="mg-input" data-testid="field-address" />
          </Field>
          <Field label="State"><input value={form.state} onChange={(e) => set("state", e.target.value)} className="mg-input" /></Field>
          <Field label="District"><input value={form.district} onChange={(e) => set("district", e.target.value)} className="mg-input" /></Field>
          <Field label="City"><input value={form.city} onChange={(e) => set("city", e.target.value)} className="mg-input" /></Field>
          <Field label="Locality"><input value={form.locality} onChange={(e) => set("locality", e.target.value)} className="mg-input" /></Field>
          <Field label="Postal code"><input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} className="mg-input" /></Field>
          <Field label="Landmark"><input value={form.landmark} onChange={(e) => set("landmark", e.target.value)} className="mg-input" /></Field>
          <Field label="Latitude"><input type="number" step="0.000001" value={form.latitude} onChange={(e) => set("latitude", e.target.value === "" ? "" : Number(e.target.value))} className="mg-input" /></Field>
          <Field label="Longitude"><input type="number" step="0.000001" value={form.longitude} onChange={(e) => set("longitude", e.target.value === "" ? "" : Number(e.target.value))} className="mg-input" /></Field>
          <div className="md:col-span-2"><button type="button" onClick={useCurrentLocation} className="mg-btn-secondary text-sm"><MapPin className="w-4 h-4" /> Use current location</button></div>
          <Field label="Description" full>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mg-input min-h-[100px]" data-testid="field-description" />
          </Field>
          <Field label="Departments (select all that apply)" full><div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-56 overflow-y-auto border rounded-xl p-3">{departmentOptions.map((department) => <label key={department} className="flex gap-2 items-center text-xs"><input type="checkbox" checked={form.departments.includes(department)} onChange={(e) => set("departments", e.target.checked ? [...form.departments,department] : form.departments.filter((value) => value !== department))} />{department}</label>)}</div></Field>
        </div>

        <div className="border-t border-slate-100 pt-4">
          <h3 className="font-bold text-mg-navy text-sm mb-3">Clinic Settings</h3>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.online_booking_enabled} onChange={(e) => set("online_booking_enabled", e.target.checked)} data-testid="field-online-booking" />
              Online booking enabled
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.patient_portal_enabled} onChange={(e) => set("patient_portal_enabled", e.target.checked)} data-testid="field-patient-portal" />
              Patient portal enabled
            </label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.public_directory_enabled} disabled={form.latitude === "" || form.longitude === ""} onChange={(e) => set("public_directory_enabled", e.target.checked)} /> Publish clinic in the public directory and map</label>
          </div>
        </div>
        <div className="border-t border-slate-100 pt-4 space-y-3">
          <h3 className="font-bold text-mg-navy text-sm">Subscription</h3>
          <p className="text-sm">{subscription?.plan_name || subscription?.plan_id || "No package"} · {subscription?.status || "No status"}{subscription?.renewal_date ? ` · renews ${subscription.renewal_date}` : ""}</p>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Package">
              <select className="mg-input" value={planChange.plan_id} onChange={(e) => setPlanChange((current) => ({ ...current, plan_id: e.target.value }))} data-testid="inline-plan-select">
                <option value="">No package</option>
                {plans.map((plan) => <option key={plan.plan_id} value={plan.plan_id}>{plan.name} · ₹{Number(plan.monthly_price || 0).toLocaleString()}/mo</option>)}
              </select>
            </Field>
            <Field label="Reason for package change">
              <input className="mg-input" value={planChange.reason} disabled={!planChange.plan_id || planChange.plan_id === subscription?.plan_id} onChange={(e) => setPlanChange((current) => ({ ...current, reason: e.target.value }))} placeholder="Required when changing package" data-testid="inline-plan-reason" />
            </Field>
          </div>
          <Link to={`/super-admin/clinics/${id}/subscription`} className="text-sm text-mg-medical font-bold inline-block">Advanced subscription controls and history →</Link>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
          <Link to={`/super-admin/clinics/${id}`} className="mg-btn-secondary text-sm">
            <X className="w-4 h-4" /> Cancel
          </Link>
          <button type="submit" disabled={busy} className="mg-btn-primary text-sm" data-testid="save-clinic-btn">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </DashboardLayout>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
